import React from 'react';

import {useTheme} from '@emotion/react';
import {
  Alert,
  SvgIcon,
  useMediaQuery,
  Divider,
  Typography,
} from '@mui/material';
import {IconCreditCard} from '@zoom/icons-react';
import {
  Box,
  Button,
  Popover,
  Stack,
} from '@zoom/react-ui';
import {IconCalendar, IconLink} from '@zoom/sun-ui-icon';
import {DateTime} from 'luxon';
import {useTranslation} from 'react-i18next';
import {useAppSelector} from 'Store/Store';

import {
  ApptHeader,
  IconContainer,
  TypographyH3,
  TypographyBody2,
  TypographyHeader,
} from './Style';

import CalendarIllustration from 'assets/svg/calendar-illustration-no-pen.svg';
import {ReactComponent as ClockOutlined} from 'assets/svg/clock-outlined.svg';
import {CardImg} from 'Components/ApptCard/Style';
import Avatar from 'Components/Avatar/index.js';
// import {InfoField} from 'Components/InfoField';
import CapacityType from 'Components/CapacityType';
import MeetingType from 'Components/MeetingType/MeetingType.js';
import OverflowTip from 'Components/OverflowTooltip';
import {TemplatePreview} from 'Components/TemplatePreview/TemplatePreview';
import useRefundTips from 'hooks/useRefundTips';
import {getCurrentLocale} from 'i18n';
import {
  formatPriceTo2Decimal, formatTimeIntervalLong, getApptType, getTimeFormatFromSetting, renderLocalTimeZone,
} from 'Utils';
import {formatApptDisplayLocation} from 'Utils/apptAvailabilityUtils';
import {getPrice, renderDuration} from 'Utils/CardDisplay.js';
import {useCustomLink} from 'Utils/integration';
import {openUrlInBrowser} from 'Utils/ZoomClientUtils';
import zoomPalette from 'zoomPalette';

const PaidSignSx = {
  display: 'inline-block',
  border: '1px solid #9ECEAD',
  height: '1.125rem',
  backgroundColor: '#F2FFF6',
  color: '#196830',
  borderRadius: '99px',
  padding: '0 5px',
  lineHeight: '1rem',
  fontSize: '10px',
  textTransform: 'uppercase',
  fontWeight: 600,
};

const autoRefundTipsSx = (theme) => ({
  fontWeight: 400,
  fontSize: '0.75rem',
  lineHeight: '1rem',
  color: theme.palette.text.neutral,
});

/**
 *
 * @param {*} props
 * @return {React.ReactElement}
 */
export function ApptFieldSnippet(props) {
  const {
    appt,
    eventInfo,
    isPaidAppt,
    showAutoRefundPolicy,
    sx,
    toCompletedScreen,
  } = props;
  const {t} = useTranslation();
  const timeFormat = useAppSelector((state) => state.hostSettingsState?.timeFormat?.value);
  const {addOnType, location} = formatApptDisplayLocation(appt);

  const price = getPrice(isPaidAppt ? null : appt, eventInfo);

  const autoRefundTips = useRefundTips();

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: toCompletedScreen ? 'row' : 'column',
      flexWrap: 'wrap',
      columnGap: '16px',
      gap: '.5rem',
      ...sx,
    }}>
      {appt?.selectedTime && <TypographyBody2 sx={{color: 'inherit', lineHeight: '1.125rem', height: '1.125rem'}}>
        <IconContainer>
          <IconCalendar/>
          <span>
            {
              DateTime
                .fromISO(appt.selectedTime)
                .setLocale(getCurrentLocale().code)
                .toFormat('EEEE, MMMM d, ' + getTimeFormatFromSetting(timeFormat, true))
            }
          </span>
        </IconContainer>

      </TypographyBody2>}

      {appt?.capacity && <TypographyBody2 sx={{color: 'inherit', lineHeight: '1.125rem', height: '1.125rem'}}>
        <CapacityType
          appointment={appt}
        />
      </TypographyBody2>}

      {appt?.duration && <TypographyBody2 sx={{color: 'inherit', lineHeight: '1.125rem', height: '1.125rem'}}>
        <IconContainer>
          <SvgIcon fontSize="inherit" component={ClockOutlined} inheritViewBox />
          <span>
            {renderDuration(appt.duration, t)}
          </span>
        </IconContainer>

      </TypographyBody2>}

      {(addOnType || location) && <TypographyBody2 sx={{
        maxWidth: '100%',
        color: 'inherit',
        lineHeight: '1.125rem',
        height: '1.125rem',
      }}>
        <MeetingType
          addOnType={addOnType}
          location={location}
          apptType={getApptType(appt)}
        />
      </TypographyBody2>}

      {price && <TypographyBody2 sx={{color: 'inherit', lineHeight: '1.125rem'}}>
        <Stack direction="row" alignItems="flex-start" gap="0.5rem">
          <IconCreditCard style={{fontSize: '16px', lineHeight: '1.125rem', position: 'relative'}} />
          <Stack>
            <Stack direction="row" alignItems="flex-start" gap="0.5rem">
              <span style={isPaidAppt ? {textDecoration: 'line-through'} : {}}>
                {formatPriceTo2Decimal(price)}
              </span>
              {isPaidAppt && <Popover content={t('payment.paidDesc')} placement="top" trigger="hover">
                <Box sx={PaidSignSx}>{t('payment.paid')}</Box>
              </Popover>}
            </Stack>
            {showAutoRefundPolicy && <Typography sx={autoRefundTipsSx}>
              {autoRefundTips}
            </Typography>}
          </Stack>
        </Stack>
      </TypographyBody2>}
    </Box>
  );
}

/**
 * Appointment Info Component
 * @param {Object} props
 * @return {ReactElement}
 */
export function ReviewOneOffApptInfo(props) {
  const {
    appt,
  } = props;

  const {t} = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const overflowTipPlacement = (isSmallScreen) ? 'bottom' : 'right';

  return (
    <ApptHeader
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '10px',
      }}
    >
      <CardImg style={{width: '76px', height: '76px'}} src={CalendarIllustration} alt="calendar"/>
      <Box sx={{display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
        <OverflowTip
          lineClamp={3}
          content={appt.summary || t('common.noTitle')}
          render={TypographyH3}
          placement={overflowTipPlacement}
        />

        <ApptFieldSnippet appt={appt} sx={{
          color: zoomPalette.gray[60],
        }} />
      </Box>
    </ApptHeader>
  );
}

/**
 * Appointment Info Component
 * @param {Object} props
 * @return {ReactElement}
 */
export default function ApptInfo(props) {
  const {
    appt,
    eventInfo,
    hideAvatar,
    hideDivider,
    isHost,
    isPaidAppt,
    oldEventInfo,
    showAutoRefundPolicy,
    toCompletedScreen,
  } = props;

  const {t} = useTranslation();
  const theme = useTheme();

  const themeFontFace = useAppSelector((state) => state.themeState.fontFaceSrcMap?.[state.themeState.fontFamily]);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const overflowTipPlacement = (isSmallScreen) ? 'bottom' : 'right';

  const createrDisplayName = useAppSelector((s) =>
    isHost? s.hostSettingsState.displayName.value: s.bookAppointments.createrDisplayName);
  const timeFormat = useAppSelector((state) => state.hostSettingsState?.timeFormat?.value);

  const customLink = useCustomLink('appt', appt);

  const boxStyle = {
    margin: '8px 0px',
    minWidth: '260px',
  };
  const nameStyle = {
    fontSize: '0.875rem',
    lineHeight: '1.5rem',
    fontWeight: '500',
  };
  const avatarStyle = {
    height: '2rem',
    width: '2rem',
    borderRadius: '10px',
    fontSize: '.75rem',
    lineHeight: '2rem',
  };

  return (
    <ApptHeader
      sx={{
        ...(toCompletedScreen && {
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          marginBottom: '32px',
          marginTop: '16px',
        }),
      }}
    >
      {!hideAvatar && <Avatar
        displayName={createrDisplayName}
        boxStyle={boxStyle}
        nameStyle={nameStyle}
        avatarStyle={avatarStyle}
      />}

      {toCompletedScreen ? (
        <Stack direction="row" alignItems="center" flexGrow={1}>
          <CardImg src={CalendarIllustration} alt="calendar"/>
          <Stack flexGrow={1} width="340px">
            <TypographyHeader noWrap sx={{width: '100%', lineHeight: '32px', fontSize: '16px', fontWeight: 590}}>
              {appt.summary}
            </TypographyHeader>
            <ApptFieldSnippet
              appt={appt}
              sx={{
                columnGap: '16px',
                color: theme.palette.text.tertiary,
              }}
              showAutoRefundPolicy={showAutoRefundPolicy}
              toCompletedScreen={toCompletedScreen}
            />
          </Stack>
          <Button size="small" type="tertiary" startIcon={<IconLink aria-hidden="true" />}
            onClick={() => openUrlInBrowser(customLink)}
          >
            {t('polls.viewBookingPage')}
          </Button>
        </Stack>
      ) : (
        <>
          <OverflowTip
            lineClamp={4}
            content={appt.summary || t('common.noTitle')}
            render={TypographyH3}
            placement={overflowTipPlacement}
            sx={{
              marginTop: '8px',
            }}
          />

          {appt.description && <TemplatePreview
            isBig={false}
            hideBackground
            noMargin
            noMarginInlineEnd
            body={appt.description}
            fontSize={14}
            customFont={themeFontFace}
            color={zoomPalette.gray[120]}
            maxHeight={120}
            bodyMargin={1}
          />}

          <ApptFieldSnippet
            appt={appt}
            eventInfo={eventInfo}
            isPaidAppt={isPaidAppt}
            sx={{
              marginTop: '8px',
              marginBottom: '8px',
              columnGap: '16px',
            }}
            showAutoRefundPolicy={showAutoRefundPolicy}
          />
        </>
      )}

      {oldEventInfo && <Alert
        icon={false}
        severity="info"
        variant="standard"
        sx={{
          borderRadius: 3,
          marginY: 1,
          fontSize: '15px',
          lineHeight: '20px',
        }}
      >
        <div style={{
          fontWeight: 500,
        }}>
          {t('rescheduling.originalBooking')}
        </div>
        <div>{formatTimeIntervalLong(
          new Date(oldEventInfo.originalEvent.start.dateTime),
          new Date(oldEventInfo.originalEvent.end.dateTime),
          timeFormat,
        )}</div>
        <div>{renderLocalTimeZone(
          new Date(oldEventInfo?.originalEvent?.start?.dateTime), null, true
        )}</div>
      </Alert>}

      {!hideDivider && <Divider sx={{margin: '12px 0px'}} />}

    </ApptHeader>
  );
}
